import React, { useEffect, useState } from "react"
import { Button, Modal } from "react-daisyui"
import BaseButton from "../components/Button/base-button"
import MultiSelectDropdown from "../features/auth/components/multi-select-dropdown"
import LoginDropdown from "../features/auth/components/login-dropdown"

type ManageGroupsProps = {
  isOpen: boolean
  handleCancel: () => void
  handleSubmit: (
    groupsToAdd: { value: string; label: string }[],
    groupToRemove: string,
  ) => void
  groupsList: { value: string; label: string }[]
  displayList: { value: string; label: string }[]
  isSubmitting: boolean
  fromGroupsPage: boolean
  defaultGroup: { value: string; label: string }[]
}

const ManageGroups: React.FC<ManageGroupsProps> = ({
  isOpen,
  handleCancel,
  handleSubmit,
  groupsList,
  displayList,
  isSubmitting,
  fromGroupsPage,
  defaultGroup,
}) => {
  const [selectedGroupToRemove, setSelectedGroupToRemove] = useState("")
  const [selectedGroupsToAdd, setSelectedGroupsToAdd] =
    useState<{ value: string; label: string }[]>(defaultGroup)

  const [isAddDisabled, setIsAddDisabled] = useState(false)
  const [isRemoveDisabled, setIsRemoveDisabled] = useState(false)

  const handleAddToGroup = (
    selectedGroups: { value: string; label: string }[],
  ) => {
    if (selectedGroupToRemove) {
      setSelectedGroupToRemove("")
    }
    setSelectedGroupsToAdd(selectedGroups)
    setIsRemoveDisabled(selectedGroups.length > 0)
  }

  const handleRemoveFromGroup = (value: string) => {
    if (value === "none") {
      setSelectedGroupToRemove("")
    } else {
      setSelectedGroupsToAdd([])
      setSelectedGroupToRemove(value)
    }
    setIsAddDisabled(value !== "none")
  }

  useEffect(() => {
    if (!isOpen) {
      setSelectedGroupToRemove("")
      setSelectedGroupsToAdd([])
      setIsAddDisabled(false)
      setIsRemoveDisabled(false)
    }
  }, [isOpen])

  useEffect(() => {
    setSelectedGroupsToAdd(defaultGroup)
  }, [defaultGroup])

  if (!isOpen) {
    return null
  }

  return (
    <Modal open={isOpen}>
      <Modal.Header className="flex justify-between items-center">
        <span className="font-bold text-lg">Manage Groups</span>
        <Button
          className="btn-sm text-gray-700 bg-base-white shadow-none border-[1px] border-solid border-gray-300 hover:text-gray-700 hover:bg-blue-light-200"
          shape="circle"
          onClick={handleCancel}>
          ✕
        </Button>
      </Modal.Header>
      <Modal.Body className="self-stretch flex flex-col items-start justify-start gap-[20px]">
        <MultiSelectDropdown
          id="addToGroup"
          titleText="Add To Group"
          options={groupsList}
          setSelectedValues={(data) => {
            handleAddToGroup(data)
          }}
          defaultSelectedOptions={selectedGroupsToAdd}
          disable={fromGroupsPage || isAddDisabled}
        />
        <LoginDropdown
          id="removeFromGroup"
          titleText="Remove From Group"
          placeholderText="Select group name"
          options={displayList}
          defaultValue={selectedGroupToRemove}
          setValue={(value: string) => handleRemoveFromGroup(value)}
          showNoneOption={true}
          disable={isRemoveDisabled}
        />
        <div>
          <strong>Note:</strong> Please add OR remove users from groups. You
          cannot add and remove at the same time.
        </div>
      </Modal.Body>
      <Modal.Actions>
        <BaseButton
          variant="primary"
          onClick={() =>
            handleSubmit(selectedGroupsToAdd, selectedGroupToRemove)
          }
          loading={isSubmitting}>
          Submit
        </BaseButton>
        <BaseButton variant="secondary" onClick={handleCancel}>
          Cancel
        </BaseButton>
      </Modal.Actions>
    </Modal>
  )
}

export default ManageGroups
